import * as React from 'react'
import { ConfirmationModal, Box, Typography, Loader } from 'cs-kevlar-react'
import { useAuth, useAuthApi } from 'hooks'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'

interface SessionExpirationWarningProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    topMargin: {
      marginTop: theme.spacing(10),
    },
  }),
)

const defaultSessionTimeoutInSeconds = 10 * 60

export const SessionExpirationWarning: React.FunctionComponent<SessionExpirationWarningProps> = () => {
  const {
    loginSessionTimeoutInMinutes,
    lastApiCall,
    sessionWarningSeconds,
    dispatch: dispatchAuthAction,
    performLogoff,
  } = useAuth()

  const classes = useStyles({})

  const { getCurrentSession } = useAuthApi()

  const [
    sessionSecondsRemaining, 
    setSessionSecondsRemaining
  ] = React.useState<number>(defaultSessionTimeoutInSeconds)

  const [
    isExtendingSession, 
    setIsExtendingSession
  ] = React.useState<boolean>(false)

  const [
    sessionExpirationStartTime, 
    setSessionExpirationStartTime
  ] = React.useState<number>(Date.now())

  const extendSession = () => {
    setIsExtendingSession(true)

    getCurrentSession(dispatchAuthAction)
      .then(() => {
        setSessionSecondsRemaining(defaultSessionTimeoutInSeconds)
        setIsExtendingSession(false)
      })
      .catch(() => {
        setIsExtendingSession(false)
      })
  }

  React.useEffect(() => {
    setSessionExpirationStartTime(Date.now())
  }, []);

  React.useEffect(() => {

    const intervalLength =
      sessionSecondsRemaining < sessionWarningSeconds ? 1000 : 10000

    const interval = setInterval(() => {
      if (lastApiCall === null) return

      const sesssionExpirationInMilliSeconds = loginSessionTimeoutInMinutes * 60 * 1000
      const now = Date.now()
      const msSinceLastApiCall = now - lastApiCall

      let millisecondsRemaining = sesssionExpirationInMilliSeconds - msSinceLastApiCall

      if(lastApiCall - sessionExpirationStartTime < sesssionExpirationInMilliSeconds / 2)
      {
        // Because we are not past half way, the cookie has not been refreshed on the server. That means lastApiCall
        // CANNOT move the window. We need to actually move the window back the same amount that has passed between
        // start time and last api call
        millisecondsRemaining -= (lastApiCall - sessionExpirationStartTime)
      }
      else
      {
        setSessionExpirationStartTime(lastApiCall)
      }

      const secondsRemaining = Math.floor(millisecondsRemaining / 1000)
      setSessionSecondsRemaining(secondsRemaining)

      if (secondsRemaining < 0) {
        setSessionSecondsRemaining(10 * 60)
        performLogoff()
        return
      }
    }, intervalLength)
    return () => {
      clearInterval(interval)
    }
  }, [lastApiCall, loginSessionTimeoutInMinutes, sessionSecondsRemaining])

  return (
    <ConfirmationModal
      onConfirm={extendSession}
      open={sessionSecondsRemaining < sessionWarningSeconds}
      showCancelButton={false}
      confirmText="OK"
      confirmButtonColor="primary"
      title="Session Expiring"
      disableEscapeKeyDown
      message={
        <Box>
          {isExtendingSession && <Loader size="sm" text="Extending Session" />}
          {!isExtendingSession && (
            <React.Fragment>
              <Typography variant="body1">
                Your session will expire in one minute. Would you like to extend
                your session?
              </Typography>
              <Typography variant="body1" className={classes.topMargin}>
                :{sessionSecondsRemaining.toString().padStart(2, '0')}
              </Typography>
            </React.Fragment>
          )}
        </Box>
      }
    />
  )
}
